import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { tierRate, cadForFcfa, fmtRate } from '@/lib/rateTiers';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ArrowRight, Shield, Clock, Zap, DollarSign, Users, Lock, Gift, Calculator, Star, TrendingUp } from 'lucide-react';
import { ReverseSendCard } from '@/components/send/ReverseSendCard';

const LOGO_URL = 'https://customer-assets-m6fa6gv7.emergentagent.net/job_crypto-remit-1/artifacts/hfmyqknz_ChatGPT%20Image%20Sep%2016%2C%202026%2C%2002_56_22%20PM.png';

export default function MboaSendLanding() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rate, setRate] = useState(null);
  const [amount, setAmount] = useState('200');
  const [simFriends, setSimFriends] = useState('5');

  const urlParams = new URLSearchParams(window.location.search);
  const refCode = urlParams.get('ref') || '';

  useEffect(() => {
    document.title = 'MboaSend — Envoyez de l\'argent du Canada vers le Cameroun';
    api.get('/mboasend/rate').then(r => setRate(r.data)).catch(() => {});
    if (refCode) localStorage.setItem('mboa_ref', refCode);
    return () => { document.title = 'MBOA'; };
  }, [refCode]);

  const tiers = rate?.rate_tiers?.length ? rate.rate_tiers : (rate?.rate_ca_cm ? [{ above_cad: 0, rate: rate.rate_ca_cm }] : []);
  const currentRate = tiers.length ? tierRate(tiers, parseFloat(amount) || 0) : 0;
  const [fcfa, setFcfa] = useState('');
  const [lastEdited, setLastEdited] = useState('cad');
  const recvAmount = amount && currentRate ? Math.round(parseFloat(amount) * currentRate) : 0;
  const onCad = (v) => { setAmount(v); setLastEdited('cad'); setFcfa(v && tiers.length ? String(Math.round(parseFloat(v) * tierRate(tiers, parseFloat(v)))) : ''); };
  const onFcfa = (v) => { setFcfa(v); setLastEdited('fcfa'); setAmount(v && tiers.length ? cadForFcfa(tiers, parseFloat(v)).toFixed(2) : ''); };
  useEffect(() => { if (currentRate && lastEdited === 'cad' && amount) setFcfa(String(Math.round(parseFloat(amount) * currentRate))); }, [currentRate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Referral simulator
  const friends = parseInt(simFriends) || 0;
  const simBonus = friends * 10;
  const simFilleulBonus = friends * 10;

  const goRegister = () => navigate(refCode ? `/mboasend/register?ref=${refCode}` : '/mboasend/register');
  const goLogin = () => navigate('/mboasend/login');
  const goDashboard = () => navigate('/send/dashboard');

  return (
    <div className="min-h-screen bg-[#FAF9F5] text-[#0D2612] overflow-x-hidden">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#FAF9F5]/90 backdrop-blur-xl border-b border-slate-200">
        <div className="container mx-auto px-4 max-w-7xl flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="MboaSend" className="w-10 h-10 rounded-lg" />
            <div>
              <span className="text-lg font-bold text-[#B8860B]" style={{ fontFamily: 'Outfit' }}>MBOA</span>
              <span className="text-lg font-light text-[#006400]" style={{ fontFamily: 'Outfit' }}>Send</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {user ? (
              <Button onClick={goDashboard} className="bg-[#006400] text-white hover:bg-[#004D00] font-semibold rounded-lg px-6">Mon compte</Button>
            ) : (
              <>
                <Button variant="ghost" className="text-slate-600 hover:text-[#0D2612]" onClick={goLogin}>Connexion</Button>
                <Button onClick={goRegister} className="bg-[#006400] text-white hover:bg-[#004D00] font-semibold rounded-lg px-6">S'inscrire</Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-28 pb-16 md:pt-36 md:pb-24">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight" style={{ fontFamily: 'Outfit' }}>
                Envoyez de l'argent du <span className="text-[#006400]">Canada</span> vers le <span className="text-[#B8860B]">Cameroun</span>
              </h1>
              <p className="text-lg text-slate-600 max-w-lg">
                Transfert rapide, sécurisé et au meilleur taux. Votre destinataire reçoit les FCFA directement sur Mobile Money.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button onClick={user ? goDashboard : goRegister} className="bg-[#006400] text-white hover:bg-[#004D00] font-bold h-14 px-8 text-base rounded-xl">
                  {user ? 'Envoyer maintenant' : 'Créer mon compte gratuit'} <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
              <div className="flex items-center gap-6 text-sm text-slate-600">
                <span className="flex items-center gap-1"><Shield className="w-4 h-4 text-[#006400]" /> Sécurisé</span>
                <span className="flex items-center gap-1"><Clock className="w-4 h-4 text-[#006400]" /> Rapide</span>
                <span className="flex items-center gap-1"><Zap className="w-4 h-4 text-[#006400]" /> Meilleur taux du marché</span>
              </div>
            </div>

            {/* Calculator */}
            <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-5">
              <div className="flex items-center gap-2 mb-2">
                <Calculator className="w-5 h-5 text-[#006400]" />
                <h3 className="font-semibold text-lg" style={{ fontFamily: 'Outfit' }}>Simulateur</h3>
              </div>

              <div className="space-y-2">
                <label className="text-sm text-slate-600">Vous envoyez (CAD)</label>
                <Input type="number" inputMode="decimal" value={amount} onChange={e => onCad(e.target.value)} placeholder="200" data-testid="sim-cad"
                  className="bg-white border-slate-300 text-[#0D2612] text-2xl h-14 text-center" style={{ fontFamily: 'JetBrains Mono' }} />
              </div>
              <div className="flex items-center gap-3 text-[11px] text-slate-400"><span className="flex-1 h-px bg-slate-200" /><ArrowRight className="w-3.5 h-3.5 rotate-90" /> ou <span className="flex-1 h-px bg-slate-200" /></div>
              <div className="space-y-2">
                <label className="text-sm text-slate-600">Votre destinataire reçoit (FCFA)</label>
                <Input type="number" inputMode="numeric" value={fcfa} onChange={e => onFcfa(e.target.value)} placeholder="82 000" data-testid="sim-fcfa"
                  className="bg-white border-[#B8860B]/40 text-[#B8860B] text-2xl h-14 text-center font-bold" style={{ fontFamily: 'JetBrains Mono' }} />
              </div>

              <div className="bg-[#F5F4EF] rounded-xl p-4 space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Taux du jour</span>
                  <span className="text-[#0D2612] font-medium" data-testid="sim-rate">{currentRate ? `1 CAD = ${fmtRate(currentRate)} FCFA` : '…'}</span>
                </div>
                {tiers.length > 1 && (
                  <p className="text-xs text-[#006400] text-right -mt-1" data-testid="sim-tiers">
                    Plus vous envoyez, meilleur est le taux : {tiers.slice(1).map(t => `dès ${t.above_cad} $ → ${fmtRate(t.rate)}`).join(' · ')}
                  </p>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Frais</span>
                  <span className="text-[#006400]">0 CAD</span>
                </div>
                <div className="border-t border-slate-200 pt-3 flex justify-between items-center">
                  <span className="text-slate-600">{lastEdited === 'fcfa' ? 'Vous envoyez' : 'Votre destinataire reçoit'}</span>
                  <span className="text-2xl font-bold text-[#B8860B]" style={{ fontFamily: 'JetBrains Mono' }} data-testid="sim-result">{lastEdited === 'fcfa' ? `${amount ? Number(amount).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0,00'} CAD` : `${recvAmount.toLocaleString('fr-FR')} FCFA`}</span>
                </div>
              </div>

              <Button onClick={user ? goDashboard : goRegister} className="w-full bg-[#006400] text-white hover:bg-[#004D00] font-bold h-12 rounded-xl">
                {user ? 'Envoyer maintenant' : 'Commencer'} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 bg-[#F0EEE6]">
        <div className="container mx-auto px-4 max-w-7xl">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-12" style={{ fontFamily: 'Outfit' }}>Comment ça marche</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: '1', title: 'Déposez des CAD', desc: 'Par virement Interac. Simple et rapide.', icon: DollarSign, color: '#006400' },
              { step: '2', title: 'Indiquez le destinataire', desc: 'Nom, numéro Mobile Money et montant. C\'est tout.', icon: Users, color: '#B8860B' },
              { step: '3', title: 'Votre proche reçoit les FCFA', desc: 'Sur son Mobile Money (MTN, Orange). En quelques minutes.', icon: Zap, color: '#006400' },
            ].map((s, i) => (
              <div key={i} className="text-center space-y-4">
                <div className="w-16 h-16 rounded-full mx-auto flex items-center justify-center" style={{ backgroundColor: `${s.color}15` }}>
                  <s.icon className="w-7 h-7" style={{ color: s.color }} />
                </div>
                <h3 className="text-lg font-semibold">{s.title}</h3>
                <p className="text-sm text-slate-600">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MBOA Rewards */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 bg-[#FFDA00]/20 border border-[#FFDA00]/50 rounded-full px-4 py-1.5">
                <Star className="w-4 h-4 text-[#B8860B]" />
                <span className="text-xs text-[#B8860B] font-medium">Programme de fidélité</span>
              </div>
              <h2 className="text-3xl font-bold" style={{ fontFamily: 'Outfit' }}>
                Gagnez des <span className="text-[#B8860B]">MBOA</span> à chaque envoi
              </h2>
              <p className="text-slate-600">
                Pour chaque dollar envoyé, vous recevez <span className="text-[#B8860B] font-bold">1 MBOA</span> (valeur : 1 FCFA).
                Accumulez des MBOA et utilisez-les pour réduire vos frais ou obtenir des bonus.
              </p>
              <div className="bg-white rounded-xl p-4 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-slate-600">Vous envoyez 200 CAD</span><span className="text-[#B8860B] font-bold">+200 MBOA</span></div>
                <div className="flex justify-between"><span className="text-slate-600">Vous envoyez 500 CAD</span><span className="text-[#B8860B] font-bold">+500 MBOA</span></div>
                <div className="flex justify-between"><span className="text-slate-600">Vous envoyez 1,000 CAD</span><span className="text-[#B8860B] font-bold">+1,000 MBOA</span></div>
              </div>
            </div>
            <div className="bg-white border border-[#FFDA00]/40 rounded-2xl p-6 space-y-4">
              <h3 className="font-semibold flex items-center gap-2"><Gift className="w-5 h-5 text-[#B8860B]" /> Parrainez et gagnez 10$</h3>
              <p className="text-sm text-slate-600">
                Invitez vos proches sur MboaSend. Quand votre filleul fait son premier envoi de 100$+ :
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-[#F5F4EF] rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-[#006400]">10$</p>
                  <p className="text-xs text-slate-600">Pour vous (parrain)</p>
                </div>
                <div className="bg-[#F5F4EF] rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-[#006400]">10$</p>
                  <p className="text-xs text-slate-600">Pour votre filleul</p>
                </div>
              </div>

              {/* Simulator */}
              <div className="border-t border-slate-200 pt-4 space-y-3">
                <h4 className="text-sm font-medium flex items-center gap-2"><TrendingUp className="w-4 h-4 text-[#B8860B]" /> Simulez vos gains</h4>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-slate-600">Je parraine</span>
                  <Input type="number" value={simFriends} onChange={e => setSimFriends(e.target.value)} min="1" max="100"
                    className="bg-white border-slate-300 text-[#0D2612] w-20 h-9 text-center text-sm" />
                  <span className="text-sm text-slate-600">amis</span>
                </div>
                <div className="bg-[#F5F4EF] rounded-lg p-3 text-sm space-y-1">
                  <div className="flex justify-between"><span className="text-slate-600">Bonus parrainage</span><span className="text-[#006400] font-bold">{simBonus}$</span></div>
                  <div className="flex justify-between"><span className="text-slate-600">Bonus pour vos filleuls</span><span className="text-[#006400]">{simFilleulBonus}$ distribués</span></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust */}
      <section className="py-16 bg-[#F0EEE6]">
        <div className="container mx-auto px-4 max-w-7xl">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-12" style={{ fontFamily: 'Outfit' }}>Pourquoi MboaSend ?</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: Lock, title: 'Sécurité maximale', desc: 'Vérification KYC, transactions cryptées. Votre argent est protégé.', color: '#006400' },
              { icon: Zap, title: 'Meilleur taux du marché', desc: 'Taux mis à jour chaque jour, aucun frais caché : le montant affiché est le montant reçu.', color: '#B8860B' },
              { icon: Users, title: 'Support humain', desc: 'Une équipe disponible sur Telegram pour vous accompagner.', color: '#006400' },
            ].map((f, i) => (
              <div key={i} className="bg-[#F5F4EF] border border-slate-200 rounded-2xl p-6 space-y-3 hover:border-[#006400]/40 transition-colors">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${f.color}15` }}>
                  <f.icon className="w-5 h-5" style={{ color: f.color }} />
                </div>
                <h3 className="font-semibold">{f.title}</h3>
                <p className="text-sm text-slate-500">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cameroun → Canada */}
      <section className="pb-4" data-testid="landing-reverse-section">
        <div className="container mx-auto px-4 max-w-4xl">
          <ReverseSendCard source="landing" variant="wide" />
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="container mx-auto px-4 max-w-3xl text-center space-y-6">
          <h2 className="text-3xl md:text-4xl font-bold" style={{ fontFamily: 'Outfit' }}>
            Prêt à envoyer de l'argent ?
          </h2>
          <p className="text-lg text-slate-600">Inscription gratuite. Premier envoi en quelques minutes.</p>
          <Button onClick={user ? goDashboard : goRegister} className="bg-[#006400] text-white hover:bg-[#004D00] font-bold h-14 px-10 text-base rounded-xl">
            {user ? 'Accéder à mon compte' : 'Créer mon compte gratuit'} <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-200 py-8">
        <div className="container mx-auto px-4 max-w-7xl space-y-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="MBOA" className="w-8 h-8 rounded-lg" />
              <span className="font-semibold text-[#B8860B]" style={{ fontFamily: 'Outfit' }}>MboaSend</span>
            </div>
            <p className="text-xs text-slate-400">© 2026 PASSERELLE NUMÉRIQUE INC. — MboaSend. Tous droits réservés.</p>
          </div>
          <div className="flex flex-wrap justify-center gap-3 text-[10px] text-slate-400">
            <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="hover:text-slate-600">Politique de confidentialité</a>
            <span>|</span>
            <a href="/terms-of-use" target="_blank" rel="noopener noreferrer" className="hover:text-slate-600">Conditions d'utilisation</a>
            <span>|</span>
            <a href="/aml-kyc-policy" target="_blank" rel="noopener noreferrer" className="hover:text-slate-600">AML/KYC</a>
            <span>|</span>
            <a href="/refund-policy" target="_blank" rel="noopener noreferrer" className="hover:text-slate-600">Remboursement</a>
            <span>|</span>
            <a href="/legal-notice" target="_blank" rel="noopener noreferrer" className="hover:text-slate-600">Mentions légales</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
